// src/components/ui/KpiCard.tsx

import React, { useEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { clsx } from "clsx";

interface KpiCardProps {
  label:    string;
  value:    number;
  prefix?:  string;
  suffix?:  string;
  format?:  "number" | "currency" | "percent" | "multiplier" | "compact";
  delta?:   number;   // % change vs prior period
  icon?:    React.ReactNode;
  iconBg?:  string;   // tailwind bg class
  sublabel?: string;
  animate?: boolean;
}

// ── Number formatter ─────────────────────────────────────────
function formatValue(
  value: number,
  format: KpiCardProps["format"],
  prefix?: string,
  suffix?: string
): string {
  let formatted = "";
  switch (format) {
    case "currency":
      if (value >= 1_000_000)      formatted = `$${(value / 1_000_000).toFixed(1)}M`;
      else if (value >= 1_000)     formatted = `$${(value / 1_000).toFixed(0)}K`;
      else                         formatted = `$${value.toFixed(0)}`;
      break;
    case "compact":
      if (value >= 1_000_000_000)  formatted = `${(value / 1_000_000_000).toFixed(1)}B`;
      else if (value >= 1_000_000) formatted = `${(value / 1_000_000).toFixed(1)}M`;
      else if (value >= 1_000)     formatted = `${(value / 1_000).toFixed(0)}K`;
      else                         formatted = value.toLocaleString();
      break;
    case "percent":
      formatted = `${value.toFixed(1)}%`;
      break;
    case "multiplier":
      formatted = `${value.toFixed(1)}x`;
      break;
    default:
      formatted = value.toLocaleString();
  }
  return `${prefix ?? ""}${formatted}${suffix ?? ""}`;
}

// ── Animated counter hook ────────────────────────────────────
function useCountUp(target: number, duration = 1000, enabled = true) {
  const [current, setCurrent] = useState(enabled ? 0 : target);
  const rafRef = useRef<number>();
  const startRef = useRef<number>();

  useEffect(() => {
    if (!enabled) { setCurrent(target); return; }
    const animate = (timestamp: number) => {
      if (!startRef.current) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, enabled]);

  return current;
}

// ── Component ────────────────────────────────────────────────
export default function KpiCard({
  label,
  value,
  prefix,
  suffix,
  format = "number",
  delta,
  icon,
  iconBg = "bg-blue/10",
  sublabel,
  animate = true,
}: KpiCardProps) {
  const animated = useCountUp(value, 900, animate);
  const displayValue = formatValue(animated, format, prefix, suffix);

  const deltaPositive = delta !== undefined && delta > 0;
  const deltaNegative = delta !== undefined && delta < 0;
  const deltaFlat     = delta !== undefined && delta === 0;

  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between">
        <p className="kpi-label">{label}</p>
        {icon && (
          <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", iconBg)}>
            {icon}
          </div>
        )}
      </div>

      <p className="kpi-value tabular mt-1">{displayValue}</p>

      {sublabel && (
        <p className="text-xs text-slate mt-0.5">{sublabel}</p>
      )}

      {delta !== undefined && (
        <div className="flex items-center gap-1 mt-1.5">
          {deltaPositive && <TrendingUp  size={12} className="text-emerald-500" />}
          {deltaNegative && <TrendingDown size={12} className="text-red-500" />}
          {deltaFlat     && <Minus        size={12} className="text-slate" />}
          <span className={clsx(
            "text-xs font-medium",
            deltaPositive && "text-emerald-600",
            deltaNegative && "text-red-500",
            deltaFlat     && "text-slate",
          )}>
            {deltaPositive ? "+" : ""}{delta.toFixed(1)}% vs last month
          </span>
        </div>
      )}
    </div>
  );
}